import type {
  BlogPost,
  BlogGenerationRequest,
  BlogGenerationResponse,
  BlogListResponse,
  NewsSource
} from './blog-types';
import { API_BASE_URL } from './config';

/**
 * Blog API Module
 * Handles fetching latest alien news and generating AI-powered blog posts
 */
export class BlogAPI {
  private static instance: BlogAPI;
  private cache: Map<string, { data: BlogPost[]; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  static getInstance(): BlogAPI {
    if (!BlogAPI.instance) {
      BlogAPI.instance = new BlogAPI();
    }
    return BlogAPI.instance;
  }

  /**
   * Fetch latest alien news from the web
   */
  async fetchLatestAlienNews(): Promise<NewsSource[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/blog/news`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.sources || [];
    } catch (error) {
      console.error('Error fetching alien news:', error);
      // Fallback to direct search if API fails
      return this.fallbackNewsSearch();
    }
  }

  /**
   * Fallback: Search for alien news using the existing search endpoint
   */
  private async fallbackNewsSearch(): Promise<NewsSource[]> {
    try {
      const queries = [
        'alien sightings latest news',
        'UFO disclosure recent',
        'extraterrestrial discovery 2024',
        'alien contact story'
      ];

      const randomQuery = queries[Math.floor(Math.random() * queries.length)];

      const response = await fetch(`${API_BASE_URL}/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: randomQuery })
      });

      const data = await response.json();

      if (data.results && data.results.length > 0) {
        return data.results.map((result: { url: string; title: string }) => ({
          title: result.title,
          url: result.url,
          snippet: '',
          date: new Date().toISOString()
        }));
      }

      return [];
    } catch (error) {
      console.error('Fallback news search failed:', error);
      return [];
    }
  }

  /**
   * Generate a blog post using AI
   */
  async generateBlogPost(request: BlogGenerationRequest): Promise<BlogGenerationResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/blog/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: BlogGenerationResponse = await response.json();

      // Clear cache when new post is generated
      this.cache.clear();

      return data;
    } catch (error) {
      console.error('Error generating blog post:', error);
      return {
        success: false,
        error: 'Failed to generate blog post. The AI mothership is offline!'
      };
    }
  }

  /**
   * Get all blog posts
   */
  async getBlogPosts(page: number = 1, limit: number = 10): Promise<BlogListResponse> {
    const cacheKey = `posts_${page}_${limit}`;
    const cached = this.cache.get(cacheKey);

    // Return cached data if still valid
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return {
        success: true,
        posts: cached.data,
        total: cached.data.length
      };
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/blog/posts?page=${page}&limit=${limit}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: BlogListResponse = await response.json();

      // Cache the results
      if (data.success && data.posts) {
        this.cache.set(cacheKey, {
          data: data.posts,
          timestamp: Date.now()
        });
      }

      return data;
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      return {
        success: false,
        message: 'Failed to load blog posts from the cosmic archives!'
      };
    }
  }

  /**
   * Get a single blog post by ID
   */
  async getBlogPost(id: string): Promise<BlogPost | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/blog/posts/${id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.post || null;
    } catch (error) {
      console.error('Error fetching blog post:', error);
      return null;
    }
  }

  /**
   * Delete a blog post (admin only)
   */
  async deleteBlogPost(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/blog/posts/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Clear cache
      this.cache.clear();

      return true;
    } catch (error) {
      console.error('Error deleting blog post:', error);
      return false;
    }
  }

  /**
   * Auto-generate a blog post from latest news
   */
  async autoGenerateBlogPost(authorWallet?: string): Promise<BlogGenerationResponse> {
    try {
      // 1. Fetch latest alien news
      const newsSources = await this.fetchLatestAlienNews();

      if (newsSources.length === 0) {
        return {
          success: false,
          message: 'No alien news found in the cosmos today!'
        };
      }

      // 2. Pick the most interesting source(s)
      const selectedSources = newsSources.slice(0, 3);

      // 3. Generate blog post with AI
      const topic = `Latest alien and UFO news: ${selectedSources[0].title}`;

      return await this.generateBlogPost({
        topic,
        sources: selectedSources,
        generateImage: true,
        authorWallet
      });
    } catch (error) {
      console.error('Error in auto-generate:', error);
      return {
        success: false,
        error: 'Auto-generation failed. Solar flares detected!'
      };
    }
  }

  /**
   * Calculate reading time for blog content
   */
  calculateReadTime(content: string): number {
    const wordsPerMinute = 200;
    const wordCount = content.trim().split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
  }
}

// Export singleton instance
export const blogAPI = BlogAPI.getInstance();
